/**
 * Support Agent Login Page
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Headphones, Mail, Lock, LogIn } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const SupportLogin = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('supportToken');
    if (token) {
      fetch(`${API_BASE}/api/support/verify`, { headers: { 'Authorization': `Bearer ${token}` } })
        .then(r => { if (r.ok) navigate('/support/dashboard'); else localStorage.removeItem('supportToken'); })
        .catch(() => localStorage.removeItem('supportToken'));
    }
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error('Enter email and password'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/support/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Login failed');
      localStorage.setItem('supportToken', d.token);
      localStorage.setItem('supportAgent', JSON.stringify(d.agent));
      toast.success(`Welcome, ${d.agent.name}!`);
      navigate('/support/dashboard');
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800 flex items-center justify-center p-4" data-testid="support-login-page">
      <Card className="w-full max-w-md bg-white/5 border-white/10 backdrop-blur-xl shadow-2xl">
        <CardHeader className="text-center pb-2">
          <div className="w-16 h-16 bg-gradient-to-br from-teal-400 to-cyan-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-teal-500/20">
            <Headphones className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-2xl font-bold text-white">Support Portal</CardTitle>
          <p className="text-slate-400 text-sm">Login to manage support tickets</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="text-sm text-slate-300 mb-1 block">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input value={email} onChange={e => setEmail(e.target.value)} placeholder="agent@example.com" type="email"
                  className="pl-10 bg-white/10 border-white/20 text-white" data-testid="support-email-input" />
              </div>
            </div>
            <div>
              <label className="text-sm text-slate-300 mb-1 block">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input value={password} onChange={e => setPassword(e.target.value)} placeholder="Password" type="password"
                  className="pl-10 bg-white/10 border-white/20 text-white" data-testid="support-password-input" />
              </div>
            </div>
            <Button type="submit" disabled={loading} className="w-full bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white" data-testid="support-login-btn">
              <LogIn className="w-4 h-4 mr-2" />{loading ? 'Logging in...' : 'Login'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default SupportLogin;
